import { useState, type ReactNode } from 'react';
import { DsButton, DsInput } from '@/modules/internal-v2/ds';
import { ContainedPage } from '@/modules/internal-v2/shared/contained-page.component';
import './calculator.css';

/** Parse a free-text numeric field; invalid / empty input counts as 0. */
function num(value: string): number {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Format a computed result; non-finite values (e.g. division by zero) show a dash. */
function fmt(value: number): string {
  return Number.isFinite(value) ? value.toFixed(2) : '–';
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  step?: number;
}

function Field({ label, value, onChange, step }: FieldProps) {
  return (
    <div className="calc-field">
      <label>{label}</label>
      <DsInput
        mono
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

interface CalcCardProps {
  title: string;
  description: string;
  children: ReactNode;
}

function CalcCard({ title, description, children }: CalcCardProps) {
  return (
    <section className="calc-card">
      <header className="calc-card-head">
        <h3>{title}</h3>
        <p className="calc-card-desc">{description}</p>
      </header>
      <div className="calc-card-body">{children}</div>
    </section>
  );
}

function Result({ value, label }: { value: string; label: string }) {
  return (
    <div className="calc-result">
      <span className="calc-result-value">{value}</span>
      <span className="calc-result-label">{label}</span>
    </div>
  );
}

function PaymentTerms() {
  const [amount, setAmount] = useState('1000');
  const [zahlungsziel, setZahlungsziel] = useState('21');
  const [interest, setInterest] = useState('7.7');

  const financingCost =
    (num(amount) * num(interest) * num(zahlungsziel)) / (100 * 365);

  return (
    <CalcCard
      title="Zahlungsziel"
      description="Dieser Zinsrechner ermittelt, wie sich ein verlängertes Zahlungsziel auf unsere Finanzierungskosten auswirkt — z. B. wenn ihr einen Kunden am Telefon habt."
    >
      <Field label="Betrag (EUR)" value={amount} onChange={setAmount} />
      <div className="calc-row">
        <Field
          label="Zahlungsziel (Tage)"
          value={zahlungsziel}
          onChange={setZahlungsziel}
        />
        <Field
          label="Zinsen (% p. a.)"
          value={interest}
          onChange={setInterest}
          step={0.01}
        />
      </div>
      <Result value={`${fmt(financingCost)} EUR`} label="Finanzierungskosten" />
    </CalcCard>
  );
}

function Margin() {
  const [ek, setEk] = useState('100');
  const [vk, setVk] = useState('110');

  const margin = (num(vk) / num(ek)) * 100 - 100;

  return (
    <CalcCard
      title="Prozentuale Marge"
      description="Berechnet die Bruttomarge in Prozent. Gib einen Einkaufs- und Verkaufspreis ein."
    >
      <Field label="Einkaufspreis (EUR)" value={ek} onChange={setEk} />
      <Field label="Verkaufspreis (EUR)" value={vk} onChange={setVk} />
      <Result value={`${fmt(margin)} %`} label="Marge" />
    </CalcCard>
  );
}

function MinimumVK() {
  const [ek, setEk] = useState('303.4');
  const [minMargin, setMinMargin] = useState('5');

  const minVk = num(minMargin) * (num(ek) / 100) + num(ek);

  return (
    <CalcCard
      title="VK + prozentuale Marge"
      description="Berechnet den Brutto-Verkaufspreis aus Einkaufspreis und gewünschter prozentualer Marge."
    >
      <Field label="Einkaufspreis (EUR)" value={ek} onChange={setEk} />
      <Field label="Mindest-Marge (%)" value={minMargin} onChange={setMinMargin} />
      <Result value={`${fmt(minVk)} EUR`} label="Mindest-Verkaufspreis" />
    </CalcCard>
  );
}

function VKSkontoJZV() {
  const [listenVk, setListenVk] = useState('303.4');
  const [skonto, setSkonto] = useState('5');
  const [jzv, setJzv] = useState('0');

  const vk =
    num(listenVk) * (1 + num(skonto) / 100) * (1 + num(jzv) / 100);

  return (
    <CalcCard
      title="VK zzgl. Skonto & JZV"
      description="Ermittelt den möglichen Verkaufspreis aus Netto-Verkaufspreis, vereinbartem Skonto-Prozentsatz und prozentualer Jahreszielvereinbarung."
    >
      <Field label="Listen-VK (EUR)" value={listenVk} onChange={setListenVk} />
      <Field label="Skonto (%)" value={skonto} onChange={setSkonto} />
      <Field
        label="Jahreszielvereinbarung (%)"
        value={jzv}
        onChange={setJzv}
      />
      <Result value={`${fmt(vk)} EUR`} label="Verkaufspreis" />
    </CalcCard>
  );
}

interface EkPair {
  ek: string;
  quantity: string;
}

function AverageEk() {
  const [pairs, setPairs] = useState<EkPair[]>([{ ek: '5', quantity: '0' }]);

  const updatePair = (index: number, patch: Partial<EkPair>) => {
    setPairs((prev) =>
      prev.map((pair, idx) => (idx === index ? { ...pair, ...patch } : pair)),
    );
  };

  const addPair = () => {
    setPairs((prev) => [...prev, { ek: '0', quantity: '0' }]);
  };

  const removePair = (index: number) => {
    setPairs((prev) => prev.filter((_, idx) => idx !== index));
  };

  const totalQuantity = pairs.reduce((sum, p) => sum + num(p.quantity), 0);
  const totalValue = pairs.reduce(
    (sum, p) => sum + num(p.ek) * num(p.quantity),
    0,
  );
  const averageEk = totalQuantity > 0 ? totalValue / totalQuantity : 0;

  return (
    <CalcCard
      title="Durchschnitts-EK"
      description="Berechnet einen mengengewichteten durchschnittlichen Einkaufspreis, bevor die Ware im System erfasst wird."
    >
      {pairs.map((pair, index) => (
        <div className="calc-pair" key={index}>
          <Field
            label={`EK ${index + 1} (EUR)`}
            value={pair.ek}
            onChange={(value) => updatePair(index, { ek: value })}
          />
          <Field
            label={`Menge ${index + 1}`}
            value={pair.quantity}
            onChange={(value) => updatePair(index, { quantity: value })}
          />
          <DsButton
            className="calc-pair-remove"
            variant="ghost"
            size="small"
            disabled={pairs.length === 1}
            onClick={() => removePair(index)}
            aria-label={`EK ${index + 1} entfernen`}
          >
            ✕
          </DsButton>
        </div>
      ))}
      <div>
        <DsButton size="small" onClick={addPair}>
          + Hinzufügen
        </DsButton>
      </div>
      <Result value={`${fmt(averageEk)} EUR`} label="Durchschnitts-EK" />
    </CalcCard>
  );
}

export function CalculatorPage() {
  return (
    <ContainedPage
      toolbar={
        <div className="calc-toolbar-content">
          <h1 className="calc-title">Rechner</h1>
          <span className="calc-subtitle">
            Kalkulationshilfen für Marge, Verkaufspreis und Zahlungsziel
          </span>
        </div>
      }
      contained
    >
      <div className="calc-grid">
        <PaymentTerms />
        <VKSkontoJZV />
        <Margin />
        <AverageEk />
        <MinimumVK />
      </div>
    </ContainedPage>
  );
}
